import React, { useEffect, useRef, useState } from 'react';
import { LockKey } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export const PASSWORD_PROMPT_USER_KEY = 'galago_password_prompt_user';
export const PASSWORD_REMINDER_PREFIX = 'galago_password_reminder_after';
export const PASSWORD_REMINDER_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

const reminderKey = (userId) => `${PASSWORD_REMINDER_PREFIX}:${userId}`;

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (_error) {
    // Private browsing and strict storage policies should not break the app.
  }
};

const removeStorage = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch (_error) {
    // Nothing else is required if browser storage is unavailable.
  }
};

export default function PasswordSetupPrompt() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const remindedUserRef = useRef(null);

  const postponeReminder = () => {
    if (!user?.id) return;
    writeStorage(
      window.localStorage,
      reminderKey(user.id),
      String(Date.now() + PASSWORD_REMINDER_INTERVAL_MS),
    );
    removeStorage(window.sessionStorage, PASSWORD_PROMPT_USER_KEY);
  };

  useEffect(() => {
    if (!user?.id) {
      setOpen(false);
      remindedUserRef.current = null;
      return undefined;
    }

    if (user.has_password) {
      setOpen(false);
      removeStorage(window.sessionStorage, PASSWORD_PROMPT_USER_KEY);
      removeStorage(window.localStorage, reminderKey(user.id));
      return undefined;
    }

    const promptUserId = readStorage(window.sessionStorage, PASSWORD_PROMPT_USER_KEY);
    if (promptUserId === user.id) {
      setOpen(true);
      return undefined;
    }

    if (remindedUserRef.current === user.id) return undefined;
    const remindAfter = Number(readStorage(window.localStorage, reminderKey(user.id)) || 0);
    if (remindAfter > Date.now()) return undefined;

    remindedUserRef.current = user.id;
    writeStorage(
      window.localStorage,
      reminderKey(user.id),
      String(Date.now() + PASSWORD_REMINDER_INTERVAL_MS),
    );
    const timer = window.setTimeout(() => {
      toast('Add a password to your account', {
        description: 'It only takes a moment and makes signing in more flexible.',
        action: {
          label: 'Add password',
          onClick: () => setOpen(true),
        },
        duration: 8000,
      });
    }, 1800);

    return () => window.clearTimeout(timer);
  }, [user?.has_password, user?.id]);

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setPassword('');
      setConfirmPassword('');
      if (!user?.has_password) postponeReminder();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setSaving(true);
      await api.post('/auth/password', { new_password: password });
      removeStorage(window.sessionStorage, PASSWORD_PROMPT_USER_KEY);
      removeStorage(window.localStorage, reminderKey(user.id));
      setPassword('');
      setConfirmPassword('');
      setOpen(false);
      await refreshUser?.();
      toast.success('Password added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add your password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="password-setup-dialog">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <LockKey className="h-5 w-5" weight="fill" />
          </div>
          <DialogTitle>Add a password</DialogTitle>
          <DialogDescription>
            Create a password to make signing in simple and secure.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="account-password">Password</Label>
            <Input
              id="account-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
              autoFocus
              data-testid="password-setup-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-password-confirm">Confirm password</Label>
            <Input
              id="account-password-confirm"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
              data-testid="password-setup-confirm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
              Not now
            </Button>
            <Button type="submit" disabled={saving} data-testid="password-setup-submit">
              {saving ? 'Adding…' : 'Add password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
